import { memo, useId, useMemo } from "react";
import { resolveFurniture, type FurnitureLike } from "../lib/furniture/resolve";
import { buildRenderPlan, type Lod } from "../lib/furniture/renderPlan";
import { toSvgPath } from "../lib/furniture/path";
import { TILE_IN } from "../lib/furniture/materials";
import { boundingBox } from "../lib/geom";

export interface FurniturePreviewProps {
  /** Anything with the resolver's inputs — a placed piece, a preset-as-furniture, a custom item. */
  furniture: FurnitureLike;
  /** Rendered size in CSS px (square viewport). */
  size?: number;
  /** Padding inside the viewport, px. */
  pad?: number;
  /** Rotation in degrees (defaults to 0 so thumbnails all face the same way). */
  rotation?: number;
  /** Detail level. Defaults to full detail. */
  lod?: Lod;
  /** Include material textures (requires canvas; falls back to flat fills). */
  textures?: boolean;
  /** Draw a soft drop shadow under the footprint. */
  shadow?: boolean;
  className?: string;
  /** Accessible label; defaults to the piece's name. */
  title?: string;
}

/**
 * SVG renderer for a piece of furniture. Shares the render plan with the
 * Konva `FurnitureNode`, so a picker card, a library thumbnail, and the
 * canvas all show the exact same geometry and materials.
 *
 * Memoized on the fields that change the picture. Position is ignored so
 * dragging a selected piece does not rebuild every inspector swatch.
 */
export const FurniturePreview = memo(function FurniturePreview({
  furniture,
  size = 64,
  pad = 6,
  rotation = 0,
  lod = 2,
  textures = true,
  shadow = true,
  className,
  title,
}: FurniturePreviewProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const resolved = resolveFurniture(furniture);
  const plan = useMemo(() => buildRenderPlan(resolved, lod, textures), [resolved, lod, textures]);

  // Fit the rotated footprint bbox into the viewport.
  const bb = useMemo(() => {
    const rad = (rotation * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    return boundingBox(resolved.footprint.map((p) => ({ x: p.x * c - p.y * s, y: p.x * s + p.y * c })));
  }, [resolved, rotation]);
  const inner = Math.max(1, size - pad * 2);
  const scale = Math.min(inner / Math.max(bb.width, 1e-6), inner / Math.max(bb.height, 1e-6));
  const cx = size / 2 - (bb.minX + bb.width / 2) * scale;
  const cy = size / 2 - (bb.minY + bb.height / 2) * scale;

  const footprintD = toSvgPath(plan.footprintCmds);
  const patterns = plan.parts.filter((pd) => pd.tile);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      role="img"
      aria-label={title ?? furniture.name}
    >
      <defs>
        {shadow && (
          <filter id={`${uid}-shadow`} x="-20%" y="-20%" width="140%" height="150%">
            <feDropShadow dx="0" dy={Math.max(0.5, scale * 0.6)} stdDeviation={Math.max(0.8, scale * 1.2)} floodColor="#0b0d14" floodOpacity="0.22" />
          </filter>
        )}
        {patterns.map((pd) => {
          const tilePx = TILE_IN * scale;
          return (
            <pattern
              key={pd.part.id}
              id={`${uid}-${pd.part.id}`}
              patternUnits="userSpaceOnUse"
              width={tilePx}
              height={tilePx}
              patternTransform={pd.patternRotation ? `rotate(${pd.patternRotation})` : undefined}
            >
              <image href={pd.tile!.dataUrl()} width={tilePx} height={tilePx} preserveAspectRatio="none" />
            </pattern>
          );
        })}
      </defs>
      <g transform={`translate(${cx} ${cy}) rotate(${rotation}) scale(${scale})`}>
        <path d={footprintD} fill={plan.baseFill} filter={shadow ? `url(#${uid}-shadow)` : undefined} />
        {plan.parts.map((pd) => (
          <path
            key={pd.part.id}
            d={toSvgPath(pd.cmds)}
            fill={pd.tile ? `url(#${uid}-${pd.part.id})` : pd.paint.fill}
            stroke={pd.paint.stroke}
            strokeWidth={0.9 / scale}
            opacity={pd.paint.opacity}
          />
        ))}
        {plan.parts.map((pd) =>
          pd.seams.map((s, i) => (
            <line
              key={`${pd.part.id}-s${i}`}
              x1={s.a.x}
              y1={s.a.y}
              x2={s.b.x}
              y2={s.b.y}
              stroke={pd.paint.seam}
              strokeWidth={0.9 / scale}
              strokeLinecap="round"
            />
          ))
        )}
        <path d={footprintD} fill="none" stroke="rgba(20,20,28,0.22)" strokeWidth={1 / scale} />
      </g>
    </svg>
  );
}, previewPropsEqual);

function previewPropsEqual(a: FurniturePreviewProps, b: FurniturePreviewProps): boolean {
  return (
    a.size === b.size &&
    a.pad === b.pad &&
    a.rotation === b.rotation &&
    a.lod === b.lod &&
    a.textures === b.textures &&
    a.shadow === b.shadow &&
    a.className === b.className &&
    a.title === b.title &&
    samePreviewFurniture(a.furniture, b.furniture)
  );
}

/** Picture equality. `x` / `y` / placed rotation are not part of the drawing. */
function samePreviewFurniture(a: FurnitureLike, b: FurnitureLike): boolean {
  if (a === b) return true;
  return (
    a.name === b.name &&
    a.width === b.width &&
    a.depth === b.depth &&
    a.height === b.height &&
    a.color === b.color &&
    a.frontKind === b.frontKind &&
    sameMaterial(a.material, b.material) &&
    JSON.stringify(a.model ?? null) === JSON.stringify(b.model ?? null) &&
    JSON.stringify(a.shape ?? null) === JSON.stringify(b.shape ?? null)
  );
}

function sameMaterial(
  a: FurnitureLike["material"],
  b: FurnitureLike["material"]
): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  return a.id === b.id && a.tint === b.tint;
}

